import Esqueleto from "../../../components/Esqueleto";
import { useState } from "react";
import Relatorio from "../../../../models/Relatorio";
import { ESTADOS_UF } from "../../../../utils/localidades";
import BarrasHorizontais from "../BarrasHorizontais";
import FiltrosLocalidade from "../FiltrosLocalidade";
import FiltrosPlantio from "../FiltrosPlantio";
import TabelaAdmin from "../../../components/TabelaAdmin";
import { COR_SERIE, Filtros, Secao } from "../painel";
import { numero, useRelatorio } from "../useRelatorio";

const LIMITE = 50;
const LIMITE_NO_GRAFICO = 15;
const INICIAL = { id: "plantios", de: "", ate: "", tipo_categoria: "", tipo: "", regiao: "", estado: "", agrupar: "estado" };

const ROTULO_AGRUPAR = { regiao: "região", estado: "estado", cidade: "cidade" };

// "Plantios por localidade": ranking por região, estado ou cidade, com período,
// categoria e tipo. Os números vêm de GET /relatorios/plantios/localidade, que já
// unifica as grafias do estado (Espírito Santo / Espirito Santo / ES).
export default function PlantiosPorLocalidade() {
  const [filtros, setFiltros] = useState(INICIAL);
  const alterar = (parte) => setFiltros((atual) => ({ ...atual, ...parte }));

  const periodoInvertido = filtros.de && filtros.ate && filtros.de > filtros.ate;
  // Cidade exige estado; o filtro já impede, isto é só a rede de segurança.
  const agrupar = filtros.agrupar === "cidade" && !filtros.estado ? "estado" : filtros.agrupar;

  const { dados, erro, carregando } = useRelatorio(
    Relatorio.plantiosPorLocalidade,
    { agrupar, de: filtros.de, ate: filtros.ate, tipo_categoria: filtros.tipo_categoria, tipo: filtros.tipo, regiao: filtros.regiao, estado: filtros.estado, limite: LIMITE },
    !periodoInvertido,
  );

  const itens = dados?.itens || [];
  const noGrafico = itens.slice(0, agrupar === "regiao" ? itens.length : LIMITE_NO_GRAFICO);
  const nomeEstado = ESTADOS_UF.find((e) => e.uf === filtros.estado)?.nome;
  const filtrado = JSON.stringify({ ...filtros, agrupar: "", id: "" }) !== JSON.stringify({ ...INICIAL, agrupar: "", id: "" });

  return (
    <Secao aria-labelledby="titulo-plantios-localidade">
      <div>
        <h3 id="titulo-plantios-localidade">Plantios por localidade</h3>
        <p className="sub">
          Agrupados por {ROTULO_AGRUPAR[agrupar]}{agrupar === "cidade" && nomeEstado ? ` — ${nomeEstado}` : ""}.
          “Não informado” reúne plantios antigos sem estado ou cidade.
        </p>
      </div>

      <Filtros>
        <FiltrosPlantio valores={filtros} onChange={alterar} />
        <FiltrosLocalidade valores={{ ...filtros, agrupar }} onChange={alterar} />
        {filtrado && <button type="button" className="limpar" onClick={() => setFiltros(INICIAL)}>Limpar filtros</button>}
        {periodoInvertido && <span className="aviso" role="alert">A data inicial é depois da final.</span>}
        {!filtros.estado && <span className="dica">Escolha um estado para ver os plantios por cidade.</span>}
      </Filtros>

      {carregando && <Esqueleto variante="barras" quantidade={8} rotulo="Carregando relatório" />}
      {erro && <div className="erro" role="alert">{erro}</div>}

      {!carregando && !erro && dados && (
        <>
          <div className="totais">
            <span><strong>{numero(dados.total.plantios)}</strong> plantios</span>
            <span><strong>{numero(dados.total.mudas)}</strong> mudas</span>
            <span><strong>{numero(dados.total.usuarios)}</strong> pessoas</span>
          </div>

          {dados.total.plantios === 0
            ? <div className="vazio">Nenhum plantio encontrado com esses filtros.</div>
            : (
              <>
                <BarrasHorizontais
                  rotulos={noGrafico.map((i) => i.label)}
                  series={[{ name: "Plantios", color: COR_SERIE, data: noGrafico.map((i) => i.plantios) }]}
                  tooltip={(i) => `<strong>${noGrafico[i].label}</strong><br/>${numero(noGrafico[i].plantios)} plantios · ${numero(noGrafico[i].mudas)} mudas · ${numero(noGrafico[i].usuarios)} pessoas`}
                  descricao={`Plantios por ${ROTULO_AGRUPAR[agrupar]}. Os valores estão na tabela abaixo.`}
                />

                <TabelaAdmin
                  rotulo={`Plantios por ${ROTULO_AGRUPAR[agrupar]}`}
                  linhas={itens}
                  chave={(i) => i.chave}
                  larguraMinima={560}
                  paginar={25} itens="localidades"
                  colunas={[
                    { id: "local", rotulo: agrupar === "regiao" ? "Região" : agrupar === "estado" ? "Estado" : "Cidade", largura: "minmax(180px, 3fr)",
                      celula: (i) => (
                        <span className="rotulo-linha">
                          {i.label}
                          {agrupar === "estado" && i.regiao && i.regiao !== "Não informado" && <small>{i.regiao}</small>}
                          {agrupar === "cidade" && i.uf && <small>{i.uf}</small>}
                        </span>
                      ) },
                    { id: "plantios", rotulo: "Plantios", largura: "minmax(90px, 1fr)", direita: true, celula: (i) => numero(i.plantios) },
                    { id: "mudas", rotulo: "Mudas", largura: "minmax(90px, 1fr)", direita: true, celula: (i) => numero(i.mudas) },
                    { id: "pessoas", rotulo: "Pessoas", largura: "minmax(90px, 1fr)", direita: true, celula: (i) => numero(i.usuarios) },
                  ]}
                />

                {dados.truncado && <p className="sub">Mostrando as {LIMITE} maiores. Use os filtros para refinar.</p>}
              </>
            )}
        </>
      )}
    </Secao>
  );
}
